import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { AmplifierModalPortal } from './AmplifierModalPortal';
import {
  AmplifierData,
  AmplifierProjectData,
  AmplifierControlData,
  Vgs,
  BandParam,
  EngParamsBackupItem,
  EngParamsBackupMessage,
} from './amplifier';
import { amplifierMqttService } from './AmplifierMqttService';
import { resolveAntennaDisplayName } from './antennaConfig';
import { createEmptyAmplifierProjectData } from './remoteControlMapper';
import {
  isProtectMaskBitSet,
  PROTECT_MASK_BITS,
  toggleProtectMaskBit,
} from './amplifierAlarmLabels';
import { VGS_RAW_MAX, VGS_VOLT_MIN, VGS_VOLT_STEP } from './engParamsScale';
import { useAmplifierI18n } from '../../i18n/amplifier/useAmplifierI18n';
import type { AmplifierLocaleKey } from '../../i18n/amplifier/locales';

const bandKeyFromIndex = (index: number): keyof BandParam => `vgs${index + 1}` as keyof BandParam;

const clampBandNo = (value: number | undefined | null): number =>
  Math.min(16, Math.max(0, Math.trunc(value ?? 0)));

type ParamFieldItem = {
  labelKey: AmplifierLocaleKey;
  field: keyof AmplifierProjectData;
  unit: string;
  fieldKey?: string;
  protectBit?: number;
  decimals?: number;
};

/** 校准系数分页 — 右列 */
const CALIBRATION_PARAM_RIGHT: Array<{
  labelKey: AmplifierLocaleKey;
  field: keyof AmplifierProjectData;
  unit: string;
}> = [
  { labelKey: '电压校准系数', field: 'voltageCalibrationCoefficient', unit: '' },
  { labelKey: '电流校准系数', field: 'currentCalibrationCoefficient', unit: '' },
  { labelKey: '频率校准系数', field: 'frequencyMeasurementCalibrationCoefficient', unit: '' },
  { labelKey: 'VGS校准系数', field: 'vgsCalibrationCoefficient', unit: '' },
  { labelKey: 'DAC参考电压', field: 'exdacRefvCalibrationMv', unit: 'mV' },
];

/** 校准系数分页 — 左列（当前频段 bandParam 子字段） */
const CALIBRATION_BAND_FIELDS: Array<{
  labelKey: AmplifierLocaleKey;
  subKey: keyof Vgs;
  unit: string;
  step: number;
  protectBit?: number;
  decimals?: number;
}> = [
  { labelKey: '偏置电压', subKey: 'vgsPower', unit: 'V', step: 0.002, decimals: 3 },
  { labelKey: '截止频率', subKey: 'frequency', unit: '', step: 1 },
  { labelKey: '输入正向功率校准系数', subKey: 'inputPositive', unit: '', step: 1 },
  { labelKey: '输入反向功率校准系数', subKey: 'inputNegative', unit: '', step: 1 },
  { labelKey: '输出正向功率校准系数', subKey: 'outputPositive', unit: '', step: 1 },
  { labelKey: '输出反向功率校准系数', subKey: 'outputNegative', unit: '', step: 1 },
  { labelKey: '功放输入功率校准', subKey: 'ampInputPowerCmp', unit: '', step: 1, protectBit: PROTECT_MASK_BITS.AMP_IN_OVER_POWER },
  { labelKey: '功放输出功率校准', subKey: 'ampOutputPowerCmp', unit: '', step: 1, protectBit: PROTECT_MASK_BITS.AMP_OUT_OVER_POWER },
];

/** 其他分页 — 左列 */
const OTHER_PARAM_LEFT: ParamFieldItem[] = [
  { labelKey: '保护电压', field: 'protectionVoltage', unit: 'V', protectBit: PROTECT_MASK_BITS.OVER_VOLTAGE },
  { labelKey: '保护电流', field: 'protectionCurrent', unit: 'A', protectBit: PROTECT_MASK_BITS.OVER_CURRENT },
  { labelKey: '保护输入正向功率', field: 'protectionInputPowerPositive', unit: 'W', protectBit: PROTECT_MASK_BITS.IN_FWD_POWER },
  { labelKey: '保护输入反向功率', field: 'protectionInputPowerNegative', unit: 'W', protectBit: PROTECT_MASK_BITS.IN_REV_POWER },
  { labelKey: '保护输出正向功率', field: 'protectionOutputPowerPositive', unit: 'W', protectBit: PROTECT_MASK_BITS.OUT_FWD_POWER },
  { labelKey: '保护输出反向功率', field: 'protectionOutputPowerNegative', unit: 'W', protectBit: PROTECT_MASK_BITS.OUT_REV_POWER },
];

/** 其他分页 — 右列 */
const OTHER_PARAM_RIGHT: ParamFieldItem[] = [
  { labelKey: '输入保护驻波比', field: 'protectionInSwr', unit: '', protectBit: PROTECT_MASK_BITS.IN_SWR, decimals: 1 },
  { labelKey: '输出保护驻波比', field: 'protectionOutSwr', unit: '', protectBit: PROTECT_MASK_BITS.OUT_SWR, decimals: 1 },
  { labelKey: '保护频率误差', field: 'protectionFrequencyError', unit: '', protectBit: PROTECT_MASK_BITS.FREQ_ERROR },
  { labelKey: '保护温度', field: 'protectionTemperature', unit: '°C', protectBit: PROTECT_MASK_BITS.OVER_TEMP },
  { labelKey: '驻波调谐值', field: 'rfTuneValue', unit: '', decimals: 1 },
  { labelKey: 'PTT触发电功率', field: 'powerTriggerPttVoltage', unit: 'W' },
];

type ParamTab = 'calibration' | 'other' | 'backup';

type BackupConfirmState =
  | { kind: 'restore'; id: string; checksum: string }
  | { kind: 'purge' }
  | { kind: 'delete'; id: string };

type EngBackupFile = {
  id: string;
  name: string;
  createdAt: string;
  checksum?: string;
  valid: boolean;
  error?: string;
};

const formatBackupTimestamp = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const parseBackupCreatedAt = (item: Pick<EngParamsBackupItem, 'created_at' | 'created_at_ms'>): string => {
  if (typeof item.created_at === 'string' && item.created_at.trim()) {
    return item.created_at.trim();
  }
  if (typeof item.created_at_ms === 'number' && Number.isFinite(item.created_at_ms)) {
    return formatBackupTimestamp(new Date(item.created_at_ms));
  }
  return formatBackupTimestamp(new Date());
};

const mapBackupItem = (item: EngParamsBackupItem): EngBackupFile | null => {
  const id = typeof item.id === 'string' ? item.id.trim() : '';
  if (!id) return null;
  return {
    id,
    name: id,
    createdAt: parseBackupCreatedAt(item),
    checksum: typeof item.checksum === 'string' && item.checksum.trim() ? item.checksum.trim() : undefined,
    valid: item.valid !== false,
    error: typeof item.error === 'string' ? item.error : undefined,
  };
};

const mapBackupList = (list: EngParamsBackupItem[] | undefined): EngBackupFile[] => {
  if (!Array.isArray(list)) return [];
  return list.map(mapBackupItem).filter((file): file is EngBackupFile => file != null);
};

export interface EngineeringDashboardModalProps {
  open: boolean;
  onClose: () => void;
  deviceId: string;
  amplifierProjectData?: AmplifierProjectData;
  amplifierData?: AmplifierData | null;
}

export const EngineeringDashboardModal: React.FC<EngineeringDashboardModalProps> = ({
  open,
  onClose,
  deviceId,
  amplifierProjectData,
  amplifierData,
}) => {
  const { t } = useAmplifierI18n();
  const [formData, setFormData] = useState<AmplifierProjectData>(createEmptyAmplifierProjectData);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [activeParamTab, setActiveParamTab] = useState<ParamTab>('calibration');
  const [backupFiles, setBackupFiles] = useState<EngBackupFile[]>([]);
  const [selectedBackupId, setSelectedBackupId] = useState<string | null>(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupConfirm, setBackupConfirm] = useState<BackupConfirmState | null>(null);

  useEffect(() => {
    if (amplifierProjectData) {
      setFormData(amplifierProjectData);
    }
  }, [amplifierProjectData]);

  useEffect(() => {
    if (!open) {
      setActiveParamTab('calibration');
      setEditingField(null);
      setSelectedBackupId(null);
      setBackupFiles([]);
      setBackupBusy(false);
      setBackupConfirm(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !deviceId) return undefined;
    return amplifierMqttService.addBackupListener(deviceId, (msg: EngParamsBackupMessage) => {
      const data = msg.data ?? {};
      const action = data.action;
      const ok = data.result === 'ok';
      if (Array.isArray(data.list)) {
        const files = mapBackupList(data.list);
        setBackupFiles(files);
        setSelectedBackupId((current) => (current && files.some((file) => file.id === current) ? current : null));
      } else if (ok && action === 'create' && typeof data.id === 'string' && data.id) {
        const created = mapBackupItem({
          id: data.id,
          created_at: data.created_at,
          created_at_ms: data.created_at_ms,
          checksum: data.checksum,
          size_bytes: data.size_bytes,
          valid: true,
        });
        if (created) {
          setBackupFiles((prev) => [created, ...prev.filter((file) => file.id !== created.id)]);
          setSelectedBackupId(created.id);
        }
      } else if (ok && action === 'delete' && typeof data.id === 'string') {
        setBackupFiles((prev) => prev.filter((file) => file.id !== data.id));
        setSelectedBackupId((current) => (current === data.id ? null : current));
      } else if (ok && action === 'purge') {
        setBackupFiles([]);
        setSelectedBackupId(null);
      }

      if (!ok) {
        message.error(data.error || t('备份操作失败'));
        setBackupBusy(false);
        return;
      }

      if (action === 'create') {
        message.success(t('备份已创建'));
      } else if (action === 'restore') {
        message.success(t('还原成功'));
        void amplifierMqttService.queryEngParams(deviceId).catch(() => undefined);
      } else if (action === 'delete') {
        message.success(t('删除备份成功'));
      } else if (action === 'purge') {
        message.success(t('清除备份成功'));
      }
      setBackupBusy(false);
    });
  }, [open, deviceId, t]);

  const displayNum = (n: number | undefined | null, decimals?: number): string => {
    const value = n ?? 0;
    return decimals != null ? value.toFixed(decimals) : String(value);
  };
  const displayUnitValue = (
    n: number | undefined | null,
    unit: string,
    decimals?: number,
  ): string => (unit ? `${displayNum(n, decimals)}${unit}` : displayNum(n, decimals));

  const getFormValue = (fieldKey: keyof AmplifierProjectData): number =>
    (formData[fieldKey] as number) ?? 0;

  const getFormValueByKey = (fieldKey: string): number => {
    if (fieldKey.startsWith('bandParam.')) {
      const [, vgsKey, subKey] = fieldKey.split('.');
      const band = formData.bandParam[vgsKey as keyof BandParam];
      return (band?.[subKey as keyof Vgs] as number) ?? 0;
    }
    return (formData as unknown as Record<string, number>)[fieldKey] ?? 0;
  };

  const buildPayloadForField = (fieldKey: string, value: number): Partial<AmplifierProjectData> => {
    if (!fieldKey.startsWith('bandParam.')) {
      return { [fieldKey]: value } as Partial<AmplifierProjectData>;
    }
    const [, vgsKey, subKey] = fieldKey.split('.');
    return {
      bandParam: {
        [vgsKey]: {
          [subKey]: value,
        },
      } as unknown as BandParam,
    };
  };

  const clampFieldValue = (fieldKey: string, value: number): number => {
    if (fieldKey.endsWith('.vgsPower')) {
      const max = VGS_VOLT_MIN + VGS_RAW_MAX * VGS_VOLT_STEP;
      return Math.min(max, Math.max(VGS_VOLT_MIN, Number(value.toFixed(3))));
    }
    return value;
  };

  const handleFieldBlurByKey = (fieldKey: string) => {
    if (editingField !== fieldKey) return;
    const num = parseFloat(editingValue) || 0;
    void handleParameterChange(buildPayloadForField(fieldKey, clampFieldValue(fieldKey, num)));
    setEditingField(null);
  };

  const handleStepperClick = (fieldKey: string, delta: number) => {
    const currentValue = getFormValueByKey(fieldKey);
    void handleParameterChange(
      buildPayloadForField(fieldKey, clampFieldValue(fieldKey, currentValue + delta)),
    );
  };

  const handleAntennaSelect = async (
    delta: Partial<Pick<AmplifierControlData, 'inputAntenna' | 'outputAntenna'>>,
  ) => {
    try {
      await amplifierMqttService.sendAmplifierControlDelta(deviceId, delta);
    } catch (error) {
      console.error('EngineeringDashboardModal: 天线切换失败:', error);
      message.error(t('天线切换失败'));
    }
  };

  const renderAntennaButton = (
    label: string,
    isActive: boolean,
    onClick: () => void,
    extraClass = '',
  ) => (
    <button
      type="button"
      className={`eng-dash-node eng-dash-node--interactive${
        isActive ? ' eng-dash-node--active' : ''
      }${extraClass ? ` ${extraClass}` : ''}`}
      onClick={onClick}
    >
      {label}
    </button>
  );

  const handleParameterChange = async (newData: Partial<AmplifierProjectData>) => {
    setFormData((prev) => {
      const next = { ...prev, ...newData };
      if (newData.bandParam) {
        next.bandParam = { ...prev.bandParam };
        (Object.keys(newData.bandParam) as Array<keyof BandParam>).forEach((key) => {
          const patch = newData.bandParam?.[key];
          if (!patch) return;
          next.bandParam[key] = { ...prev.bandParam[key], ...patch };
        });
      }
      return next;
    });
    try {
      await amplifierMqttService.sendProjectParametersDelta(deviceId, newData);
    } catch (error) {
      console.error('EngineeringDashboardModal: 工程参数下发失败:', error);
    }
  };

  const handleProtectMaskToggle = (bit: number, shielded: boolean) => {
    void handleParameterChange({
      protectMask: toggleProtectMaskBit(formData.protectMask, bit, shielded),
    });
  };

  const handleFieldBlur = (fieldKey: keyof AmplifierProjectData, editingKey: string) => {
    if (editingField !== editingKey) return;
    const num = parseFloat(editingValue) || 0;
    void handleParameterChange({ [fieldKey]: num });
    setEditingField(null);
  };

  const handleFactoryReset = () => {
    message.info(t('功能待对接'));
  };

  const handleSyncEngParams = () => {
    amplifierMqttService.queryEngParams(deviceId).then(() => {
      message.success(t('数据同步指令已发送'));
    }).catch((err) => {
      console.error('EngineeringDashboardModal: 数据同步失败:', err);
      message.error(t('数据同步发送失败'));
    });
  };

  const isRemoteControlData = amplifierData?._source === 'remote_control';
  const measurementDivisor = isRemoteControlData ? 1 : 100;
  const swrDivisor = isRemoteControlData ? 1 : 10;

  const displayVoltage = amplifierData?.currentVoltage !== undefined
    ? amplifierData.currentVoltage / measurementDivisor
    : 0;
  const displayCurrent = amplifierData?.currentCurrent !== undefined
    ? amplifierData.currentCurrent / measurementDivisor
    : 0;
  const displayInputSwr = amplifierData?.inputRf !== undefined
    ? amplifierData.inputRf / swrDivisor
    : 1;
  const displayOutputSwr = amplifierData?.outputRf !== undefined
    ? amplifierData.outputRf / swrDivisor
    : 1;

  const inputAntenna = amplifierData?.inputAntenna ?? 0;
  const outputAntenna = amplifierData?.outputAntenna ?? 0;
  const powerOn = amplifierData?.power === 1;
  const antennaConfig = amplifierData?.antennaConfig;

  const getAntennaButtonLabel = (index: number, fallback: string) =>
    resolveAntennaDisplayName(index, antennaConfig, fallback);

  const renderProtectMaskCheckbox = (bit: number) => (
    <label className="eng-dash-protect-mask">
      <input
        type="checkbox"
        checked={isProtectMaskBitSet(formData.protectMask, bit)}
        onChange={(e) => handleProtectMaskToggle(bit, e.target.checked)}
      />
      {t('屏蔽')}
    </label>
  );

  const renderParamInput = (item: ParamFieldItem) => {
    const editingKey = item.fieldKey ?? String(item.field);
    return (
      <div key={editingKey} className="eng-dash-param-row">
        <span className="eng-dash-param-row__label">{t(item.labelKey)}</span>
        <input
          className="eng-dash-param-row__input"
          value={
            editingField === editingKey
              ? editingValue
              : displayUnitValue(getFormValue(item.field), item.unit, item.decimals)
          }
          onFocus={() => {
            setEditingField(editingKey);
            setEditingValue(displayNum(getFormValue(item.field), item.decimals));
          }}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={() => handleFieldBlur(item.field, editingKey)}
        />
        {item.protectBit !== undefined ? renderProtectMaskCheckbox(item.protectBit) : null}
      </div>
    );
  };

  const renderParamColumn = (items: ParamFieldItem[]) => (
    <div className="eng-dash-params__column">
      {items.map((item) => renderParamInput(item))}
    </div>
  );

  const renderCalibrationStepperRow = (
    label: string,
    fieldKey: string,
    unit: string,
    labelClass = '',
    protectBit?: number,
    step = 1,
    decimals?: number,
  ) => (
    <div key={fieldKey} className="eng-dash-stepper-row">
      <span className={`eng-dash-stepper-row__label${labelClass ? ` ${labelClass}` : ''}`}>
        {label}
      </span>
      <button
        type="button"
        className="eng-dash-stepper-row__btn"
        onClick={() => handleStepperClick(fieldKey, -step)}
        aria-label={t('减少 {label}', { label })}
      >
        -
      </button>
      <input
        className="eng-dash-stepper-row__value"
        value={
          editingField === fieldKey
            ? editingValue
            : displayUnitValue(getFormValueByKey(fieldKey), unit, decimals)
        }
        onFocus={() => {
          setEditingField(fieldKey);
          setEditingValue(displayNum(getFormValueByKey(fieldKey), decimals));
        }}
        onChange={(e) => setEditingValue(e.target.value)}
        onBlur={() => handleFieldBlurByKey(fieldKey)}
      />
      <button
        type="button"
        className="eng-dash-stepper-row__btn"
        onClick={() => handleStepperClick(fieldKey, step)}
        aria-label={t('增加 {label}', { label })}
      >
        +
      </button>
      {protectBit !== undefined ? renderProtectMaskCheckbox(protectBit) : null}
    </div>
  );

  const currentBandNo = clampBandNo(formData.currentBandNo);
  const selectedBandKey = bandKeyFromIndex(currentBandNo);

  const renderCalibrationPanel = () => (
    <div className="eng-dash-params__columns eng-dash-params__columns--calibration">
      <div className="eng-dash-params__column eng-dash-params__column--calibration-left">
        <div className="eng-dash-stepper-row eng-dash-stepper-row--band">
          <span className="eng-dash-stepper-row__label eng-dash-stepper-row__label--wide">{t('频段')}</span>
          <span className="eng-dash-stepper-row__band-value" aria-label={t('频段')}>
            {`BAND${currentBandNo + 1}`}
          </span>
        </div>
        {CALIBRATION_BAND_FIELDS.map((item) =>
          renderCalibrationStepperRow(
            t(item.labelKey),
            `bandParam.${selectedBandKey}.${item.subKey}`,
            item.unit,
            'eng-dash-stepper-row__label--wide',
            item.protectBit,
            item.step,
            item.decimals,
          ),
        )}
      </div>
      <div className="eng-dash-params__column">
        {CALIBRATION_PARAM_RIGHT.map((item) =>
          renderCalibrationStepperRow(t(item.labelKey), String(item.field), item.unit),
        )}
      </div>
    </div>
  );

  const selectedBackup = backupFiles.find((file) => file.id === selectedBackupId) ?? null;
  const canRestoreSelected = Boolean(selectedBackup?.valid && selectedBackup.checksum);

  const sendBackupCommand = async (
    data: Parameters<typeof amplifierMqttService.sendEngParamsBackup>[1],
  ) => {
    if (backupBusy) return;
    if (!deviceId) {
      message.error(t('备份指令发送失败'));
      return;
    }
    setBackupBusy(true);
    try {
      await amplifierMqttService.sendEngParamsBackup(deviceId, data);
    } catch (error) {
      console.error('EngineeringDashboardModal: eng_params_backup 发送失败:', error);
      setBackupBusy(false);
      message.error(t('备份指令发送失败'));
    }
  };

  const handleCreateBackup = () => {
    void sendBackupCommand({ action: 'create' });
  };

  const handleRestoreSelected = () => {
    if (!selectedBackup?.valid || !selectedBackup.checksum) {
      message.warning(t('该备份校验失败，无法还原'));
      return;
    }
    setBackupConfirm({
      kind: 'restore',
      id: selectedBackup.id,
      checksum: selectedBackup.checksum,
    });
  };

  const handleClearAllBackups = () => {
    if (backupFiles.length === 0) return;
    setBackupConfirm({ kind: 'purge' });
  };

  const handleDeleteBackup = (fileId: string) => {
    setBackupConfirm({ kind: 'delete', id: fileId });
  };

  const backupConfirmTitle =
    backupConfirm?.kind === 'restore'
      ? t('确认还原选中备份？')
      : backupConfirm?.kind === 'purge'
        ? t('确认清除全部备份？')
        : backupConfirm?.kind === 'delete'
          ? t('确认删除该备份？')
          : '';

  const handleBackupConfirmOk = () => {
    if (!backupConfirm) return;
    const pending = backupConfirm;
    setBackupConfirm(null);
    if (pending.kind === 'restore') {
      void sendBackupCommand({
        action: 'restore',
        id: pending.id,
        checksum: pending.checksum,
      });
      return;
    }
    if (pending.kind === 'purge') {
      void sendBackupCommand({ action: 'purge' });
      return;
    }
    void sendBackupCommand({ action: 'delete', id: pending.id });
  };

  const renderBackupPanel = () => (
    <div className="eng-dash-backup">
      <div className="eng-dash-backup__toolbar">
        <button
          type="button"
          className="eng-dash-action-btn"
          disabled={backupBusy}
          onClick={handleCreateBackup}
        >
          {t('立即备份')}
        </button>
        <button
          type="button"
          className="eng-dash-action-btn"
          disabled={backupBusy || !canRestoreSelected}
          onClick={handleRestoreSelected}
        >
          {t('还原选中')}
        </button>
        <button
          type="button"
          className="eng-dash-action-btn"
          disabled={backupBusy || backupFiles.length === 0}
          onClick={handleClearAllBackups}
        >
          {t('清除全部')}
        </button>
      </div>
      {backupFiles.length === 0 ? (
        <div className="eng-dash-backup__empty">{backupBusy ? t('正在处理备份') : t('暂无备份')}</div>
      ) : (
        <ul className="eng-dash-backup__list">
          {backupFiles.map((file) => {
            const selected = file.id === selectedBackupId;
            return (
              <li key={file.id}>
                <button
                  type="button"
                  className={`eng-dash-backup__row${selected ? ' eng-dash-backup__row--selected' : ''}${
                    file.valid ? '' : ' eng-dash-backup__row--invalid'
                  }`}
                  onClick={() => setSelectedBackupId(file.id)}
                >
                  <span className="eng-dash-backup__radio" aria-hidden>
                    {selected ? '●' : '○'}
                  </span>
                  <span className="eng-dash-backup__meta">
                    <span className="eng-dash-backup__time">{file.createdAt}</span>
                    <span className="eng-dash-backup__name">{file.name}</span>
                    {!file.valid && (
                      <span className="eng-dash-backup__error">
                        {file.error || t('备份不可用')}
                      </span>
                    )}
                  </span>
                </button>
                <button
                  type="button"
                  className="eng-dash-backup__delete"
                  disabled={backupBusy}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteBackup(file.id);
                  }}
                >
                  {t('删除备份')}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <AmplifierModalPortal open={open} layer="elevated">
      <div
        className="engineering-dashboard-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="engineering-dashboard-modal__scroll">
        <section className="eng-dash-params">
          <div className="eng-dash-params__tabs" role="tablist" aria-label={t('工程参数分页')}>
            <button
              type="button"
              role="tab"
              aria-selected={activeParamTab === 'calibration'}
              className={`eng-dash-params__tab${
                activeParamTab === 'calibration' ? ' eng-dash-params__tab--active' : ''
              }`}
              onClick={() => {
                setActiveParamTab('calibration');
                setEditingField(null);
                void amplifierMqttService.queryEngParams(deviceId).catch((err) => {
                  console.error('EngineeringDashboardModal: 工程参数查询失败:', err);
                  message.error(t('数据同步发送失败'));
                });
              }}
            >
              {t('校准系数')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeParamTab === 'other'}
              className={`eng-dash-params__tab${
                activeParamTab === 'other' ? ' eng-dash-params__tab--active' : ''
              }`}
              onClick={() => {
                setActiveParamTab('other');
                setEditingField(null);
                void amplifierMqttService.queryEngParams(deviceId).catch((err) => {
                  console.error('EngineeringDashboardModal: 工程参数查询失败:', err);
                  message.error(t('数据同步发送失败'));
                });
              }}
            >
              {t('其他')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeParamTab === 'backup'}
              className={`eng-dash-params__tab${
                activeParamTab === 'backup' ? ' eng-dash-params__tab--active' : ''
              }`}
              onClick={() => {
                setActiveParamTab('backup');
                setEditingField(null);
                void sendBackupCommand({ action: 'list' });
              }}
            >
              {t('备份')}
            </button>
          </div>

          <div
            className="eng-dash-params__panel"
            role="tabpanel"
            aria-label={
              activeParamTab === 'calibration'
                ? t('校准系数')
                : activeParamTab === 'other'
                  ? t('其他')
                  : t('备份')
            }
          >
            {activeParamTab === 'calibration' ? (
              renderCalibrationPanel()
            ) : activeParamTab === 'other' ? (
              <div className="eng-dash-params__columns">
                {renderParamColumn(OTHER_PARAM_LEFT)}
                {renderParamColumn(OTHER_PARAM_RIGHT)}
              </div>
            ) : (
              renderBackupPanel()
            )}
          </div>
        </section>

        <section className="eng-dash-actions">
          <button type="button" className="eng-dash-action-btn" onClick={handleFactoryReset}>
            {t('恢复出厂设置')}
          </button>
          <button type="button" className="eng-dash-action-btn" onClick={handleSyncEngParams}>
            {t('数据同步')}
          </button>
          <button type="button" className="eng-dash-action-btn" onClick={onClose}>
            Exit
          </button>
        </section>

        <section className="eng-dash-monitor">
          <div className="eng-dash-monitor__log">
            <span className="eng-dash-monitor__log-text">{t('Command send/receive log')}</span>
          </div>

          <div className="eng-dash-monitor__path">
            <div className="eng-dash-path-layout">
              <div className="eng-dash-path-col eng-dash-path-col--in">
                {renderAntennaButton(getAntennaButtonLabel(0, 'IN1'), inputAntenna === 1, () => {
                  void handleAntennaSelect({ inputAntenna: 1 });
                })}
                {renderAntennaButton(getAntennaButtonLabel(1, 'IN2'), inputAntenna === 2, () => {
                  void handleAntennaSelect({ inputAntenna: 2 });
                })}
              </div>

              <span
                className={`eng-dash-node eng-dash-node--pa${
                  powerOn ? ' eng-dash-node--active' : ''
                }`}
              >
                PA
              </span>

              <div className="eng-dash-path-ant-grid">
                {renderAntennaButton(
                  getAntennaButtonLabel(2, 'ANT1'),
                  outputAntenna === 1,
                  () => {
                    void handleAntennaSelect({ outputAntenna: 1 });
                  },
                  'eng-dash-path-ant-grid__ant1',
                )}
                {renderAntennaButton(
                  getAntennaButtonLabel(3, 'ANT2'),
                  outputAntenna === 2,
                  () => {
                    void handleAntennaSelect({ outputAntenna: 2 });
                  },
                  'eng-dash-path-ant-grid__ant2',
                )}
                {renderAntennaButton(
                  getAntennaButtonLabel(4, 'ANT3'),
                  outputAntenna === 3,
                  () => {
                    void handleAntennaSelect({ outputAntenna: 3 });
                  },
                  'eng-dash-path-ant-grid__ant3',
                )}
                {renderAntennaButton(
                  getAntennaButtonLabel(5, 'ANT4'),
                  outputAntenna === 4,
                  () => {
                    void handleAntennaSelect({ outputAntenna: 4 });
                  },
                  'eng-dash-path-ant-grid__ant4',
                )}
              </div>
            </div>
          </div>

          <div className="eng-dash-monitor__electrical">
            <div className="eng-dash-electrical-item">
              <span className="eng-dash-electrical-item__label">{t('电压:')}</span>
              <span className="eng-dash-electrical-item__value">{displayVoltage.toFixed(2)} V</span>
            </div>
            <div className="eng-dash-electrical-item">
              <span className="eng-dash-electrical-item__label">{t('电流:')}</span>
              <span className="eng-dash-electrical-item__value">{displayCurrent.toFixed(2)} A</span>
            </div>
          </div>

          <div className="eng-dash-monitor__power-block">
            <div className="eng-dash-power-block__title">{t('输入 <100W')}</div>
            <div className="eng-dash-power-block__main">
              {amplifierData?.inputPowerPositive !== undefined
                ? `${Math.round(amplifierData.inputPowerPositive)}W`
                : '0W'}
            </div>
            <div className="eng-dash-power-block__sub">
              <span>{t('反向功率')} {amplifierData?.inputPowerNegative ?? 0}W</span>
              <span>{t('驻波')} {displayInputSwr.toFixed(1)}</span>
            </div>
          </div>

          <div className="eng-dash-monitor__power-block">
            <div className="eng-dash-power-block__title">{t('输出')}</div>
            <div className="eng-dash-power-block__main">
              {amplifierData?.outputPowerPositive !== undefined
                ? `${Math.round(amplifierData.outputPowerPositive)}W`
                : '0W'}
            </div>
            <div className="eng-dash-power-block__sub">
              <span>{t('反向功率')} {amplifierData?.outputPowerNegative ?? 0}W</span>
              <span>{t('驻波')} {displayOutputSwr.toFixed(1)}</span>
            </div>
          </div>
        </section>
        </div>
        {backupConfirm ? (
          <div
            className="eng-dash-confirm"
            role="dialog"
            aria-modal="true"
            aria-label={backupConfirmTitle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="eng-dash-confirm__card">
              <p className="eng-dash-confirm__title">{backupConfirmTitle}</p>
              <div className="eng-dash-confirm__actions">
                <button
                  type="button"
                  className="eng-dash-action-btn"
                  onClick={() => setBackupConfirm(null)}
                >
                  {t('取消')}
                </button>
                <button
                  type="button"
                  className="eng-dash-action-btn"
                  onClick={handleBackupConfirmOk}
                >
                  {t('确认')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </AmplifierModalPortal>
  );
};

export default EngineeringDashboardModal;
